/* FLM — Profile hub ("Your hub"), pixel-ported from the canvas artboard
   (Dating App/canvas/screens-profile.jsx → ScrProfileOwn): strength ring
   avatar, stats row, premium banner (free → upsell ladder; premium → REAL
   plan name + its icon + "Active · N days left"), settings rows (photos /
   verification / privacy / language / wallet). Mint accent converted to orange
   via --accent tokens. The working language switcher (lang/setLang) lives in
   the Language row, where the artboard puts it.
   NOTE: there is NO "FLM Black" plan — plans are free<spark<pulse<infinity. */
import React from 'react';
import { Icon, Sheet, PhotoSlot, Img } from '../components/ui';
import { PhotoCropper, optimizeImage } from '../components/photo-cropper';
import { VoiceRecorder } from '../components/voice-recorder';
import { FLM_DATA } from '../data/mock';
import { isDemo } from '../lib/demo';
import { StatePanel, ProfileSkeleton } from '../lib/feedback';
import {
  getMe, getMyStats, getPlans, getSubscription, mediaUrl, patchProfile,
  postBoost,
  getInterests, putInterests, getMyPhotos, uploadPhoto, deletePhoto, getReferrals,
} from '../api/client';
import { haptic, openTelegramLink } from '../lib/telegram';

/* Owner feedback 2026-06: ghost mode / privacy is parked for now — the row is
   gated (not deleted) so flipping this constant brings it back wired. */
const GHOST_ENABLED = false;

/* Owner decision 2026-06: the standalone "TON Connect / Pay-per-use,
   non-custodial" wallet row is confusing — payments still happen at checkout
   (boost + premium), so the row is hidden here. Code stays gated (not deleted)
   so flipping this constant brings the row back. */
const TON_CONNECT_ROW_ENABLED = false;

/* canvas .flm-card/.flm-row/.flm-col are taken by other production styles,
   so this screen inlines them 1:1 (same approach as onboarding.jsx) */
const CARD = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 16,
  boxShadow: 'var(--shadow-card)',
  position: 'relative',
};
const ROW = { display: 'flex', alignItems: 'center' };
const COL = { display: 'flex', flexDirection: 'column' };

/* canvas .flm-ava.g1 gradient + head-and-shoulders silhouette shade */
const G1 = 'linear-gradient(160deg, #7A1FA2, #2D0041 70%)';
const AVA_SHADE =
  'radial-gradient(38% 30% at 50% 38%, rgba(0,0,0,.32), transparent 72%), ' +
  'radial-gradient(85% 52% at 50% 108%, rgba(0,0,0,.38), transparent 70%)';

function Blob({ c, style }) {
  /* canvas .flm-blob = blur(70px); .radial-glow defaults to 80 — override */
  return <span className={`radial-glow ${c}`} style={{ filter: 'blur(70px)', ...style }}></span>;
}

/* TON gem mark — canvas flm-ui.jsx TonMark (filled brand glyph, screen-local) */
function TonMark({ size = 17 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flex: 'none', display: 'block' }} aria-hidden="true">
      <path d="M4.2 4h15.6c.9 0 1.5 1 1 1.8l-7.8 13.7a1.16 1.16 0 0 1-2 0L3.2 5.8C2.7 5 3.3 4 4.2 4z" fill="currentColor"></path>
      <path d="M12 5.6v11.2" stroke="#000" strokeOpacity=".35" strokeWidth="1.4"></path>
    </svg>
  );
}

/* canvas .flm-hr divider */
function Hr() {
  return <hr style={{ height: 1, background: 'var(--border)', border: 'none', margin: 0 }} />;
}

/* settings row — canvas ScrProfileOwn Item, 1:1 */
function Item({ icon, label, sub, accent, badge, trailing, onClick }) {
  return (
    <div className="flm-row" onClick={onClick} style={{ ...ROW, gap: 13, padding: '13px 16px' }}>
      <span style={{
        width: 36, height: 36, borderRadius: 12, border: '1px solid var(--border)',
        background: 'var(--surface-2)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', color: accent || 'var(--text-2)', flex: 'none',
      }}>{icon}</span>
      <div style={{ ...COL, flex: 1, minWidth: 0 }}>
        <b style={{ fontSize: 14.5 }}>{label}</b>
        <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{sub}</span>
      </div>
      {badge ? (
        <span className="flm-mono" style={{
          fontSize: 8.5, color: 'var(--accent)', flex: 'none',
          border: '1px solid rgba(255,212,145,.4)', padding: '3px 8px', borderRadius: 99,
        }}>{badge}</span>
      ) : null}
      {trailing !== undefined
        ? trailing
        : <span className="flm-flip" style={{ ...ROW, color: 'var(--text-3)' }}><Icon name="chevRight" size={16} /></span>}
    </div>
  );
}

/* ---------- Boost row — reflects the FREE weekly allowance (per plan
   entitlements.boostsPerWeek). Layout follows the canvas Item exactly (36px
   tinted icon, title/subtitle, trailing chip/chevron) but adds a thin accent
   progress bar of free boosts used this week. States:
     • active boost   → "Boosted — seen first until HH:MM" + Active badge, tap inert
     • free left      → "N of M free boosts left · resets <when>" + progress bar
     • exhausted/free  → paid-boost framing (opens the buy sheet on tap)
   `justGranted` runs the one-shot activation pulse after a free claim. */
function BoostRow({
  t, faNum, hasFreeBoosts, perWeek, used, remaining, resetWhen,
  active, untilLabel, boosting, justGranted, onClick,
}) {
  const showAllowance = hasFreeBoosts && !active; /* bar only when a free tier & not mid-boost */
  const pct = perWeek > 0 ? Math.min(100, Math.round((used / perWeek) * 100)) : 0;

  const sub = active
    ? t('hubBoostUntil', { time: untilLabel })
    : hasFreeBoosts
      ? (remaining > 0
          ? t('hubBoostFreeLeft', { n: faNum(remaining), m: faNum(perWeek), when: resetWhen })
          : t('hubBoostNoneLeft', { when: resetWhen }))
      : t('hubBoostSub');

  return (
    <div className="flm-row" onClick={active || boosting ? undefined : onClick}
      style={{ ...COL, gap: 9, padding: '13px 16px', cursor: active || boosting ? 'default' : 'pointer' }}>
      <div style={{ ...ROW, gap: 13 }}>
        <span style={{
          width: 36, height: 36, borderRadius: 12, border: '1px solid var(--border)',
          background: justGranted ? 'rgba(255,212,145,.16)' : 'var(--surface-2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--gold)', flex: 'none',
          transform: justGranted ? 'scale(1.12)' : 'scale(1)',
          transition: 'transform .35s var(--ease-out-expo), background .35s ease',
        }}>
          <Icon name="zap" size={17} fill={active || justGranted ? 'var(--gold)' : 'none'} />
        </span>
        <div style={{ ...COL, flex: 1, minWidth: 0 }}>
          <b style={{ fontSize: 14.5 }}>{t('hubBoost')}</b>
          <span style={{ fontSize: 12.5, color: active ? 'var(--accent)' : 'var(--text-3)' }}>{sub}</span>
        </div>
        {active ? (
          <span className="flm-mono" style={{
            fontSize: 8.5, color: 'var(--accent)', flex: 'none',
            border: '1px solid rgba(255,212,145,.4)', padding: '3px 8px', borderRadius: 99,
          }}>{t('hubBoostActive')}</span>
        ) : boosting ? (
          <span className="flm-mono" style={{ fontSize: 8.5, color: 'var(--text-3)', flex: 'none' }}>{t('verifying')}</span>
        ) : (
          <span className="flm-flip" style={{ ...ROW, color: 'var(--text-3)' }}><Icon name="chevRight" size={16} /></span>
        )}
      </div>
      {showAllowance ? (
        /* free-boosts-used-this-week bar — accent fill on the surface track */
        <div style={{ height: 5, borderRadius: 99, background: 'var(--surface-2)', overflow: 'hidden' }}>
          <span style={{
            display: 'block', height: '100%', width: `${pct}%`,
            background: 'var(--accent)', borderRadius: 99,
            transition: 'width .4s var(--ease-out-expo)',
          }} />
        </div>
      ) : null}
    </div>
  );
}

/* slug → "Title Case" label (same helper the chat screen uses for interests) */
const slugLabel = (slug) => String(slug).replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const slugOf = (label) => String(label).toLowerCase().replace(/\s+/g, '-');

/* ---------- Optional dating attributes — option lists MIRROR the server DTO
   (apps/api/.../dto/profiles.dto.ts). Each enum field maps its values to an
   i18n key so both the edit selects here and the read-only facts grid in
   discover.jsx render localized labels. Keep these two in sync with the DTO. */
const FREQ_KEY = (v) => 'attrFreq_' + v;
export const ATTR_FIELDS = [
  { key: 'bodyType', label: 'attrBodyType', opts: ['slim', 'athletic', 'average', 'curvy', 'plus'], optKey: (v) => 'attrBody_' + v },
  { key: 'relationshipGoal', label: 'attrRelationshipGoal', opts: ['serious', 'casual', 'friends', 'unsure'], optKey: (v) => 'attrGoal_' + v },
  { key: 'wantsChildren', label: 'attrWantsChildren', opts: ['yes', 'no', 'maybe', 'have'], optKey: (v) => 'attrKids_' + v },
  { key: 'smoking', label: 'attrSmoking', opts: ['never', 'sometimes', 'often'], optKey: FREQ_KEY },
  { key: 'drinking', label: 'attrDrinking', opts: ['never', 'socially', 'often'], optKey: FREQ_KEY },
  { key: 'exercise', label: 'attrExercise', opts: ['never', 'sometimes', 'often'], optKey: FREQ_KEY },
  { key: 'diet', label: 'attrDiet', opts: ['omnivore', 'vegetarian', 'vegan', 'halal', 'kosher', 'other'], optKey: (v) => 'attrDiet_' + v },
  { key: 'pets', label: 'attrPets', opts: ['none', 'dog', 'cat', 'other'], optKey: (v) => 'attrPets_' + v },
  { key: 'education', label: 'attrEducation', opts: ['high_school', 'college', 'bachelors', 'masters', 'phd', 'other'], optKey: (v) => 'attrEdu_' + v },
  { key: 'zodiac', label: 'attrZodiac', opts: ['aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo', 'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces'], optKey: (v) => 'attrZodiac_' + v },
];
/** Localized label for an attribute value (or null if unknown/unset). */
export function attrValueLabel(t, fieldKey, value) {
  if (!value) return null;
  const f = ATTR_FIELDS.find((x) => x.key === fieldKey);
  if (!f) return null;
  return t(f.optKey(value));
}

/* Single-select chip row — reuses the wizard's flm-chip; tapping the active
   chip again clears it (sends '' → server clears the column). */
function AttrChips({ label, value, opts, optKey, t, onChange }) {
  return (
    <div style={{ ...COL, gap: 8 }}>
      <span className="flm-label">{label}</span>
      <div style={{ ...ROW, flexWrap: 'wrap', gap: 8 }}>
        {opts.map((o) => {
          const on = value === o;
          return (
            <button key={o} className={'flm-chip' + (on ? ' is-on' : '')} onClick={() => onChange(on ? '' : o)} style={{ cursor: 'pointer' }}>
              {on ? <Icon name="check" size={11} strokeWidth={3} /> : null}
              {t(optKey(o))}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Edit profile sheet — bio / job / interests / photos / voice.
   Same silent mock-fallback as every screen: API failures keep current state.
   Photos ride the SAME pipeline as the wizard: pick → optimizeImage (≤1600px
   JPEG .85, EXIF stripped) → PhotoCropper (cover = round avatar mask, others
   4:5) → upload; a live failure shows the wizard's inline obUploadErr note. */
function EditProfileSheet({ open, t, faNum, me, onClose, onSaved }) {
  const [bio, setBio] = React.useState('');
  const [job, setJob] = React.useState('');
  const [picked, setPicked] = React.useState([]); /* interest slugs */
  const [pool, setPool] = React.useState(null); /* taxonomy slugs (GET /v1/interests) */
  const [photos, setPhotos] = React.useState([]);
  const [crop, setCrop] = React.useState(null); /* { src, round } while the crop overlay is up */
  const [upErr, setUpErr] = React.useState(false); /* live photo upload failed → inline note */
  const [checking, setChecking] = React.useState(false); /* AI moderation in flight on a new upload */
  const fileInput = React.useRef(null);
  /* optional dating attributes — one editable map; seeded from me, diffed on save */
  const [attrs, setAttrs] = React.useState({});
  const seeded = React.useRef({}); /* the seed snapshot we diff against on save */
  const [aboutOpen, setAboutOpen] = React.useState(false); /* "About you" collapsible */
  const [langDraft, setLangDraft] = React.useState(''); /* the language text input */

  /* seed from the loaded profile each time the sheet opens; refresh from the API */
  React.useEffect(() => {
    if (!open) return;
    setBio(me?.profile?.bio || '');
    setJob(me?.profile?.job || '');
    setPicked((me?.profile?.interests || []).map((i) => i.slug));
    setPhotos(me?.profile?.photos || []);
    setCrop(null);
    setUpErr(false);
    /* attribute seed — copy the editable keys (string fields + numbers +
       languages[]); '' / [] for the unset ones so the diff on save is clean */
    const p = me?.profile || {};
    const seed = {
      heightCm: p.heightCm ?? '',
      weightKg: p.weightKg ?? '',
      religion: p.religion ?? '',
      languages: Array.isArray(p.languages) ? [...p.languages] : [],
    };
    for (const f of ATTR_FIELDS) seed[f.key] = p[f.key] ?? '';
    seeded.current = { ...seed, languages: [...seed.languages] };
    setAttrs(seed);
    setLangDraft('');
    setAboutOpen(false);
    getInterests()
      .then((res) => { if (Array.isArray(res) && res.length > 0) setPool(res.map((i) => i.slug)); })
      .catch(() => {});
    getMyPhotos()
      .then((res) => { if (Array.isArray(res?.items)) setPhotos(res.items); })
      .catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const setAttr = (key, value) => setAttrs((a) => ({ ...a, [key]: value }));
  const addLang = (raw) => {
    const v = String(raw || '').trim();
    setLangDraft('');
    if (!v) return;
    setAttrs((a) => {
      const cur = a.languages || [];
      if (cur.length >= 10 || cur.some((x) => x.toLowerCase() === v.toLowerCase())) return a;
      return { ...a, languages: [...cur, v] };
    });
  };
  const removeLang = (v) => setAttrs((a) => ({ ...a, languages: (a.languages || []).filter((x) => x !== v) }));

  /* taxonomy: live slugs (mock pool only in demo mode) — picked slugs always rendered */
  const list = React.useMemo(() => {
    const base = pool || (isDemo() ? FLM_DATA.interestPool.map(slugOf) : []);
    return [...new Set([...base, ...picked])];
  }, [pool, picked]);
  /* wizard cap: 5 interests (toggle off always allowed) */
  const toggle = (s) => setPicked((p) => (p.includes(s) ? p.filter((x) => x !== s) : p.length < 5 ? [...p, s] : p));

  /* ---- photo pipeline (wizard parity): pick → optimize → crop → upload ---- */
  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUpErr(false);
    const round = photos.length === 0; // first photo becomes the cover → round avatar crop
    optimizeImage(file)
      .then((src) => setCrop({ src, round }))
      .catch(() => setCrop({ src: URL.createObjectURL(file), round })); // crop still re-encodes on confirm
  };
  const cropCancel = () => {
    if (crop) URL.revokeObjectURL(crop.src);
    setCrop(null);
  };
  const cropDone = (blob) => {
    URL.revokeObjectURL(crop.src);
    setCrop(null);
    const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
    // Photo runs the AI moderation at upload — show a "checking" indicator, then
    // append on accept or surface the rejection reason on refuse.
    setUpErr('');
    setChecking(true);
    uploadPhoto(file)
      .then((ph) => { setPhotos((ps) => [...ps, ph]); onSaved(); /* hub + checklist stay live */ })
      .catch((e) => setUpErr(String((e && e.message) || t('obUploadErr'))))
      .finally(() => setChecking(false));
  };
  const removePhoto = (id) => {
    // A profile must keep at least 2 photos — refuse client-side (the server also
    // enforces it) and tell the user to add one first.
    if (photos.length <= 2) { setUpErr(t('photoMinRemove')); return; }
    const prev = photos;
    setUpErr('');
    setPhotos((ps) => ps.filter((ph) => ph.id !== id)); /* optimistic */
    deletePhoto(id)
      .then(() => onSaved())
      .catch((e) => { setPhotos(prev); setUpErr(String((e && e.message) || t('photoMinRemove'))); });
  };

  /* Build the profile patch: bio/job always, plus ONLY the attributes that
     changed vs the seed. Numbers go as ints (or '' to clear); enum strings as
     the slug (or '' to clear); languages as the array when it differs. */
  const buildPatch = () => {
    const patch = { bio, job };
    const base = seeded.current;
    const numKey = (k) => {
      const raw = attrs[k];
      const cleared = raw === '' || raw === null || raw === undefined;
      const next = cleared ? '' : Number(raw);
      const prev = base[k] === '' || base[k] == null ? '' : Number(base[k]);
      if (next !== prev && !(typeof next === 'number' && Number.isNaN(next))) patch[k] = next;
    };
    numKey('heightCm');
    numKey('weightKg');
    for (const f of ATTR_FIELDS) {
      const next = attrs[f.key] ?? '';
      if (next !== (base[f.key] ?? '')) patch[f.key] = next;
    }
    /* religion is a free string (not in ATTR_FIELDS) — diff it the same way */
    const nextRel = (attrs.religion ?? '').trim();
    if (nextRel !== (base.religion ?? '')) patch.religion = nextRel;
    const nextLangs = attrs.languages || [];
    const baseLangs = base.languages || [];
    if (nextLangs.length !== baseLangs.length || nextLangs.some((x, i) => x !== baseLangs[i])) {
      patch.languages = nextLangs;
    }
    return patch;
  };

  const save = () => {
    /* allSettled never rejects — failures silently keep the hub's current data */
    Promise.allSettled([patchProfile(buildPatch()), putInterests(picked)]).then(() => onSaved());
    onClose();
  };

  return (
    <React.Fragment>
    <Sheet open={open} onClose={onClose}>
      <div data-screen-label="Edit profile">
        <span className="flm-eyebrow">
          <span className="flm-dot"></span>
          <span className="flm-mono" style={{ color: 'var(--text-2)' }}>{t('profile')}</span>
        </span>
        <h3 className="flm-display" style={{ fontSize: 27, color: 'var(--text)', margin: '12px 0 14px' }}>{t('editProfile')}</h3>

        {/* bio + job */}
        <div style={{ ...COL, gap: 8 }}>
          <span className="flm-label">{t('about')}</span>
          <textarea className="flm-input" rows={3} maxLength={500} value={bio} placeholder={t('epBioPh')}
            onChange={(e) => setBio(e.target.value)}
            style={{ resize: 'none', fontSize: '0.94rem', lineHeight: 1.45 }} />
          <span className="flm-label" style={{ marginTop: 6 }}>{t('job')}</span>
          <input className="flm-input" maxLength={80} value={job} placeholder={t('epJobPh')}
            onChange={(e) => setJob(e.target.value)} style={{ fontSize: '0.94rem' }} />
        </div>

        {/* interests — wizard chip pattern (StepTaste) */}
        <div style={{ ...COL, gap: 8, marginTop: 16 }}>
          <span className="flm-label">{t('interests')} · {faNum(picked.length)}/{faNum(5)}</span>
          <div style={{ ...ROW, flexWrap: 'wrap', gap: 8 }}>
            {list.map((s) => {
              const on = picked.includes(s);
              return (
                <button key={s} className={'flm-chip' + (on ? ' is-on' : '')} onClick={() => toggle(s)} style={{ cursor: 'pointer' }}>
                  {on ? <Icon name="check" size={11} strokeWidth={3} /> : null}
                  {slugLabel(s)}
                </button>
              );
            })}
          </div>
        </div>

        {/* photos — current set + add/delete (cap = admin `media.max_photos`,
            default 10; server-enforced too). The add slot stays until the cap. */}
        <div style={{ ...COL, gap: 8, marginTop: 16 }}>
          <span className="flm-label">{t('photos')}</span>
          <input ref={fileInput} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            {photos.map((ph) => (
              <div key={ph.id} style={{ position: 'relative', height: 96, borderRadius: 14, overflow: 'hidden', border: '1px solid var(--border)' }}>
                <Img src={mediaUrl(ph.url)} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                {ph.isCover ? (
                  <span className="flm-mono" style={{ position: 'absolute', bottom: 5, insetInlineStart: 5, fontSize: 7.5, color: 'var(--accent)', background: 'rgba(0,0,0,0.55)', padding: '2px 6px', borderRadius: 99 }}>{t('obMainPhoto')}</span>
                ) : null}
                <button className="flm-ghost-btn" onClick={() => removePhoto(ph.id)} style={{ position: 'absolute', top: 5, insetInlineEnd: 5, width: 22, height: 22, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                  <Icon name="x" size={12} />
                </button>
              </div>
            ))}
            {photos.length < (me?.maxPhotos || 10) ? (
              <PhotoSlot placeholder="+" onPick={() => fileInput.current?.click()} style={{ height: 96 }} />
            ) : null}
          </div>
          <span className="flm-mono" style={{ fontSize: 8, color: 'var(--text-3)' }}>{faNum(photos.length)} / {faNum(me?.maxPhotos || 10)}</span>
          {checking ? (
            /* AI moderation in flight — the photo is verified before it's added */
            <div style={{ ...ROW, gap: 8 }}>
              <span className="flm-photo-spin" style={{ width: 14, height: 14 }} />
              <span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{t('obAiChecking')}</span>
            </div>
          ) : null}
          {upErr ? (
            /* live upload failed/rejected — show WHY (AI photo check) so the user
               knows to replace it; falls back to the generic note */
            <div style={{ ...ROW, gap: 8 }}>
              <Icon name="x" size={14} color="var(--love)" />
              <span style={{ fontSize: 12.5, color: 'var(--love)' }}>{upErr === true ? t('obUploadErr') : upErr}</span>
            </div>
          ) : null}
        </div>

        {/* voice intro — same recorder card as the wizard; a saved intro
            (me.profile.voiceUrl) seeds the play/delete state, re-record replaces */}
        <div style={{ ...COL, marginTop: 16 }}>
          <VoiceRecorder t={t} faNum={faNum}
            initial={me?.profile?.voiceUrl
              ? { url: mediaUrl(me.profile.voiceUrl), duration: me.profile.voiceDuration || 0 }
              : null}
            onUploaded={() => onSaved()} onDeleted={() => onSaved()} />
        </div>

        {/* About you — optional dating attributes, grouped under a collapsible
            header so they never overwhelm the sheet. All optional; saving sends
            only the fields that changed (buildPatch diffs against the seed). */}
        <div style={{ ...COL, gap: 8, marginTop: 16 }}>
          <button className="flm-ghost-btn" onClick={() => setAboutOpen((v) => !v)}
            style={{ ...ROW, gap: 8, padding: '6px 0', cursor: 'pointer', textAlign: 'start' }}>
            <span className="flm-label" style={{ flex: 1 }}>{t('aboutYou')}</span>
            <span className="flm-flip" style={{ ...ROW, color: 'var(--text-3)', transform: aboutOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s ease' }}>
              <Icon name="chevDown" size={16} />
            </span>
          </button>
          {aboutOpen ? (
            <div style={{ ...COL, gap: 16 }}>
              <span style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: -4 }}>{t('aboutYouSub')}</span>

              {/* height + weight — number inputs with unit suffix labels */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ ...COL, gap: 8 }}>
                  <span className="flm-label">{t('attrHeight')}</span>
                  <div style={{ ...ROW, gap: 8 }}>
                    <input className="flm-input" type="number" inputMode="numeric" min={120} max={230}
                      value={attrs.heightCm ?? ''} placeholder={t('attrHeightPh')}
                      onChange={(e) => setAttr('heightCm', e.target.value)} style={{ fontSize: '0.94rem', flex: 1, minWidth: 0 }} />
                    <span className="flm-mono" style={{ fontSize: 10, color: 'var(--text-3)', flex: 'none' }}>{t('attrUnitCm')}</span>
                  </div>
                </div>
                <div style={{ ...COL, gap: 8 }}>
                  <span className="flm-label">{t('attrWeight')}</span>
                  <div style={{ ...ROW, gap: 8 }}>
                    <input className="flm-input" type="number" inputMode="numeric" min={30} max={300}
                      value={attrs.weightKg ?? ''} placeholder={t('attrWeightPh')}
                      onChange={(e) => setAttr('weightKg', e.target.value)} style={{ fontSize: '0.94rem', flex: 1, minWidth: 0 }} />
                    <span className="flm-mono" style={{ fontSize: 10, color: 'var(--text-3)', flex: 'none' }}>{t('attrUnitKg')}</span>
                  </div>
                </div>
              </div>

              {/* enum-like fields — single-select chip rows */}
              {ATTR_FIELDS.map((f) => (
                <AttrChips key={f.key} label={t(f.label)} value={attrs[f.key] ?? ''}
                  opts={f.opts} optKey={f.optKey} t={t} onChange={(v) => setAttr(f.key, v)} />
              ))}

              {/* languages — multi-select via text input → chips (cap 10) */}
              <div style={{ ...COL, gap: 8 }}>
                <span className="flm-label">{t('attrLanguages')} · {faNum((attrs.languages || []).length)}/{faNum(10)}</span>
                {(attrs.languages || []).length ? (
                  <div style={{ ...ROW, flexWrap: 'wrap', gap: 8 }}>
                    {(attrs.languages || []).map((lng) => (
                      <button key={lng} className="flm-chip is-on" onClick={() => removeLang(lng)} style={{ cursor: 'pointer' }}>
                        {lng}<Icon name="x" size={11} strokeWidth={2.5} />
                      </button>
                    ))}
                  </div>
                ) : null}
                {(attrs.languages || []).length < 10 ? (
                  <input className="flm-input" maxLength={20} value={langDraft} placeholder={t('attrLanguagesPh')}
                    onChange={(e) => setLangDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLang(langDraft); } }}
                    onBlur={() => addLang(langDraft)} style={{ fontSize: '0.94rem' }} />
                ) : null}
              </div>

              {/* religion — free-ish short text */}
              <div style={{ ...COL, gap: 8 }}>
                <span className="flm-label">{t('attrReligion')}</span>
                <input className="flm-input" maxLength={40} value={attrs.religion ?? ''} placeholder={t('attrReligionPh')}
                  onChange={(e) => setAttr('religion', e.target.value)} style={{ fontSize: '0.94rem' }} />
              </div>
            </div>
          ) : null}
        </div>

        <button className="flm-cta" onClick={save} style={{ width: '100%', marginTop: 18 }}>{t('save')}</button>
      </div>
    </Sheet>
    {open && crop ? (
      /* crop overlay — rendered as a SIBLING of the Sheet so it escapes the
         sheet's scroll container / stacking context (z-90 over the app frame,
         exactly how the wizard mounts it outside its scroll area) */
      <PhotoCropper src={crop.src} round={crop.round} aspect={crop.round ? 1 : 4 / 5}
        title={t('obCropTitle')} hint={t('obCropHint')} zoomLabel={t('obZoom')}
        previewLabel={t('obCropAvatar')} confirmLabel={t('obCropUse')} cancelLabel={t('cancel')}
        faNum={faNum} onCancel={cropCancel} onConfirm={cropDone} />
    ) : null}
    </React.Fragment>
  );
}

/* ---------- Profile-strength checklist — opened from the "PROFILE n% COMPLETE"
   line. Mirrors the server's strengthOf() EXACTLY (profiles.service.ts): base
   30 for the name/birthday/gender trio, +20 five interests, +10 bio, +10 job,
   +15 voice intro, +15 first photo — so the checklist always agrees with the
   percentage. Undone rows deep-link into the edit sheet (it owns every fix). */
function StrengthSheet({ open, t, faNum, me, pct, onClose, onFix }) {
  const p = me?.profile;
  const items = [
    { key: 'csBasics', pts: 30, done: Boolean(p) },
    { key: 'csPhoto', pts: 15, done: (p?.photos?.length ?? p?.photoCount ?? 0) > 0 },
    { key: 'csInterests', pts: 20, done: (p?.interests?.length ?? 0) >= 5 },
    { key: 'csBio', pts: 10, done: Boolean(p?.bio) },
    { key: 'csJob', pts: 10, done: Boolean(p?.job) },
    { key: 'csVoice', pts: 15, done: Boolean(p?.hasVoiceIntro) },
  ];
  return (
    <Sheet open={open} onClose={onClose}>
      <div data-screen-label="Profile strength">
        <span className="flm-eyebrow">
          <span className="flm-dot"></span>
          <span className="flm-mono" style={{ color: 'var(--text-2)' }}>{t('hubProfileComplete', { pct: faNum(pct) })}</span>
        </span>
        <h3 className="flm-display" style={{ fontSize: 27, color: 'var(--text)', margin: '12px 0 4px' }}>{t('csTitle')}</h3>
        <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{t('csSub')}</span>
        <div style={{ ...CARD, ...COL, overflow: 'hidden', marginTop: 14 }}>
          {items.map((it, i) => (
            <React.Fragment key={it.key}>
              {i > 0 ? <Hr /> : null}
              <div className="flm-row" onClick={it.done ? undefined : onFix}
                style={{ ...ROW, gap: 12, padding: '12px 16px', cursor: it.done ? 'default' : 'pointer', opacity: it.done ? 0.55 : 1 }}>
                <span style={{
                  width: 22, height: 22, borderRadius: '50%', flex: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: it.done ? 'var(--accent)' : 'transparent',
                  border: it.done ? 'none' : '1.5px solid var(--border-bright)',
                }}>
                  {it.done ? <Icon name="check" size={12} color="#000" strokeWidth={3} /> : null}
                </span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 14, color: 'var(--text)' }}>{t(it.key)}</span>
                <span className="flm-mono" style={{ fontSize: 9, color: it.done ? 'var(--text-3)' : 'var(--accent)', flex: 'none' }}>
                  {t('csPct', { n: faNum(it.pts) })}
                </span>
                {!it.done ? (
                  <span className="flm-flip" style={{ ...ROW, color: 'var(--text-3)' }}><Icon name="chevRight" size={14} /></span>
                ) : null}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </Sheet>
  );
}

/* Boost purchase sheet — its own lazy chunk so TON Connect + qrcode load only when it opens. */
const BoostSheet = React.lazy(() => import('./boost-sheet').then((m) => ({ default: m.BoostSheet })));

/* Small premium pill — the plan's own icon reused near the name. Tasteful: a
   hairline accent pill, matching the premium banner. Shows the REAL plan name
   (uppercased) from getSubscription's plan.name (e.g. "SPARK") with a generic
   "PREMIUM" fallback, and the plan's icon (from getSubscription's plan.icon —
   e.g. infinity→'infinity'), falling back to the gold crown when none. */
function PremiumBadge({ t, planName, planIcon }) {
  const label = planName ? planName.toUpperCase() : t('premiumBadgeFallback');
  const icon = planIcon || 'crown';
  return (
    <span style={{
      ...ROW, gap: 4, flex: 'none',
      padding: '2px 8px', borderRadius: 99,
      border: '1px solid rgba(255,212,145,.4)',
      background: 'rgba(255,212,145,.08)',
    }}>
      <Icon name={icon} size={11} color="var(--gold)" fill={icon === 'crown' ? 'var(--gold)' : 'none'} style={{ flex: 'none' }} />
      <span className="flm-mono" style={{ fontSize: 8.5, color: 'var(--accent)' }}>{label}</span>
    </span>
  );
}

/* Tier rank order (mirrors premium.jsx): free<spark<pulse<infinity. Unknown
   paid tiers sort last so a future tier still reads as an upgrade. Used to
   decide whether the manage sheet offers an Upgrade (not already top tier). */
const TIER_RANK = { free: 0, spark: 1, pulse: 2, infinity: 3 };
const tierRank = (id) => (id in TIER_RANK ? TIER_RANK[id] : 99);
const TOP_TIER_RANK = 3; /* infinity — no upgrade above it */

/* ---------- Manage-plan sheet — opened by tapping the premium banner. Reads
   GET /v1/me/subscription (passed in as `sub`): for PREMIUM shows the REAL plan
   name + its icon, status, days remaining, expiry, perks and the effective
   entitlements, PLUS Renew/Extend (re-purchase the current tier) and — when not
   already top tier — Upgrade to a higher tier; both route to the premium ladder
   (onUpgrade, which auto-detects the active sub and shows renew + upgrade). FREE
   shows the upsell + what they'd unlock and the free limits, CTA = the full
   ladder. Null-safe: while `sub` loads it falls back to isPremium for framing. */
function ManagePlanSheet({ open, t, faNum, sub, isPremium, lang, onClose, onUpgrade }) {
  const premium = sub ? sub.isPremium : isPremium;
  const ent = sub?.entitlements;
  const daysRemaining = sub?.daysRemaining ?? 0;
  const planName = sub?.plan?.name || t('premiumBadgeFallback');
  const planIcon = sub?.plan?.icon || 'crown';
  const perks = sub?.plan?.perks ?? [];
  /* not already top tier → an Upgrade option is offered alongside Renew */
  const canUpgrade = premium && tierRank(sub?.plan?.id) < TOP_TIER_RANK;

  /* localized expiry date — "Renews <date>" / free → "No active plan" */
  const expiryLabel = sub?.expiresAt
    ? faNum(new Date(sub.expiresAt).toLocaleDateString(lang === 'fa' ? 'fa-IR' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' }))
    : null;

  /* days-left line: singular/plural, faNum-ed (guarded — 0 never prints "0 days left") */
  const daysLabel = daysRemaining > 0
    ? (daysRemaining === 1 ? t('mpDayLeft') : t('mpDaysLeft', { n: faNum(daysRemaining) }))
    : t('mpStatusActive');

  /* one entitlement row (icon + label) */
  const LimitRow = ({ icon, label }) => (
    <div style={{ ...ROW, gap: 10, padding: '9px 0' }}>
      <span style={{
        width: 26, height: 26, borderRadius: 8, flex: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: premium ? 'rgba(255,212,145,.1)' : 'var(--surface-2)',
        color: premium ? 'var(--accent)' : 'var(--text-3)',
        border: '1px solid var(--border)',
      }}>{icon}</span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: 'var(--text-2)' }}>{label}</span>
    </div>
  );

  /* number-or-"Unlimited" formatter for the per-day caps */
  const cap = (v, freeKey) => (v === 'unlimited' ? null : t(freeKey, { n: faNum(v) }));

  return (
    <Sheet open={open} onClose={onClose}>
      <div data-screen-label="Manage plan">
        <span className="flm-eyebrow">
          <span className="flm-dot"></span>
          <span className="flm-mono" style={{ color: 'var(--text-2)' }}>{t('mpEyebrow')}</span>
        </span>
        <h3 className="flm-display" style={{ fontSize: 27, color: 'var(--text)', margin: '12px 0 4px', display: 'flex', alignItems: 'center', gap: 9 }}>
          {/* header mark = the plan's icon when premium (crown fallback); crown when free */}
          <Icon name={premium ? planIcon : 'crown'} size={22} color="var(--gold)"
            fill={(premium ? planIcon : 'crown') === 'crown' ? 'var(--gold)' : 'none'} />
          {premium ? t('mpTitlePremium') : t('mpTitleFree')}
        </h3>

        {premium ? (
          <React.Fragment>
            {/* plan + status + days remaining + expiry */}
            <div style={{ ...CARD, ...COL, gap: 8, padding: '14px 16px', marginTop: 12, background: 'linear-gradient(135deg, var(--primary-tint), transparent 60%)', borderColor: 'rgba(122,31,162,.5)' }}>
              <div style={{ ...ROW, gap: 8 }}>
                {/* the plan's own icon beside its real name (matches the banner + name badge) */}
                <Icon name={planIcon} size={17} color="var(--gold)" fill={planIcon === 'crown' ? 'var(--gold)' : 'none'} style={{ flex: 'none' }} />
                <b style={{ fontSize: 16, flex: 1 }}>{planName}</b>
                <span className="flm-mono" style={{ fontSize: 8.5, color: 'var(--accent)', flex: 'none', border: '1px solid rgba(255,212,145,.4)', padding: '3px 8px', borderRadius: 99 }}>{t('mpStatusActive')}</span>
              </div>
              <span className="flm-display" style={{ fontSize: 22, color: 'var(--accent)' }}>{daysLabel}</span>
              {expiryLabel ? (
                <span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{t('mpExpires', { date: expiryLabel })}</span>
              ) : null}
            </div>
          </React.Fragment>
        ) : (
          <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{t('mpFreeSub')}</span>
        )}

        {/* perks list (from the plan) — only when the catalog carries them */}
        {perks.length ? (
          <div style={{ ...COL, gap: 0, marginTop: 16 }}>
            <span className="flm-label">{t('mpPerksTitle')}</span>
            <div style={{ ...COL, gap: 0, marginTop: 6 }}>
              {perks.map((k) => (
                <div key={k} style={{ ...ROW, gap: 9, padding: '7px 0' }}>
                  <Icon name="check" size={14} color="var(--accent)" strokeWidth={3} style={{ flex: 'none' }} />
                  <span style={{ fontSize: 13.5, color: 'var(--text-2)' }}>{t(k)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* effective limits / entitlements — reads the live entitlements block */}
        {ent ? (
          <div style={{ ...COL, gap: 0, marginTop: 16 }}>
            <span className="flm-label">{t('mpLimitsTitle')}</span>
            <div style={{ ...CARD, ...COL, padding: '4px 16px', marginTop: 6 }}>
              <LimitRow icon={<Icon name="heart" size={14} />}
                label={ent.likesPerDay === 'unlimited' ? t('mpLikesUnlimited') : cap(ent.likesPerDay, 'mpLikesFree')} />
              <Hr />
              <LimitRow icon={<Icon name="zap" size={14} />}
                label={ent.superLikesPerDay === 'unlimited' ? t('mpSuperLikesUnlimited') : cap(ent.superLikesPerDay, 'mpSuperLikesFree')} />
              <Hr />
              <LimitRow icon={<Icon name="message" size={14} />}
                label={ent.messagesPerDay === 'unlimited' ? t('mpMessagesUnlimited') : cap(ent.messagesPerDay, 'mpMessagesFree')} />
              <Hr />
              <LimitRow icon={<Icon name={ent.canSeeLikers ? 'badgeCheck' : 'eyeOff'} size={14} />}
                label={ent.canSeeLikers ? t('mpSeeLikers') : t('mpSeeLikersFree')} />
              <Hr />
              <LimitRow icon={<Icon name="rewind" size={14} />}
                label={ent.canRewind ? t('mpRewind') : t('mpRewindFree')} />
              <Hr />
              <LimitRow icon={<Icon name="zap" size={14} />}
                label={t('mpBoost', { n: faNum(ent.boostDurationMin) })} />
            </div>
          </div>
        ) : null}

        {/* CTA(s):
            • premium → Renew/Extend the current tier, plus Upgrade (only when
              not already top tier). Both open the premium ladder, which itself
              renders the current plan as a Renew card + the higher tiers.
            • free → the full upgrade ladder. */}
        {premium ? (
          <div style={{ ...COL, gap: 9, marginTop: 18 }}>
            <button className="flm-cta" onClick={onUpgrade} style={{ width: '100%', gap: 8 }}>
              <Icon name={planIcon} size={17} fill={planIcon === 'crown' ? 'currentColor' : 'none'} />
              {t('mpRenew')}
            </button>
            {canUpgrade ? (
              <button className="flm-ghost-btn" onClick={onUpgrade} style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '12px 14px', borderRadius: 14, border: '1px solid var(--accent)',
                background: 'rgba(255,212,145,0.08)', color: 'var(--accent)', cursor: 'pointer',
              }}>
                <Icon name="crown" size={16} />
                {t('mpUpgradeHigher')}
              </button>
            ) : null}
          </div>
        ) : (
          <button className="flm-cta" onClick={onUpgrade} style={{ width: '100%', marginTop: 18, gap: 8 }}>
            <Icon name="crown" size={17} />
            {t('mpUpgrade')}
          </button>
        )}
      </div>
    </Sheet>
  );
}

/* Invite & earn — share a referral deep-link; both sides get bonus sparks when an
   invited friend finishes onboarding. Tapping Share opens Telegram's share sheet. */
const REFERRAL_BOT = 'FindLoveMateBot';
function InviteCard({ t, faNum }) {
  const [ref, setRef] = React.useState(null);
  React.useEffect(() => {
    if (isDemo()) return;
    getReferrals().then((r) => r && setRef(r)).catch(() => {});
  }, []);
  if (!ref || ref.enabled === false) return null;
  // Main Mini App deep-link: t.me/<bot>?startapp=<param> (no app short-name path).
  const link = `https://t.me/${REFERRAL_BOT}?startapp=${ref.startParam}`;
  const premium = ref.rewardKind === 'premium';
  const subText = premium
    ? t('inviteSubPremium', { n: faNum(ref.premiumDays ?? 0), plan: ref.planName || t('premiumBadge') })
    : t('inviteSub', { n: faNum(ref.rewardPerInvite ?? 0) });
  // Compact reward badge: "3d Infinity" or "+3 ✨".
  const rewardBadge = premium
    ? `${faNum(ref.premiumDays ?? 0)}d ${ref.planName || ''}`.trim()
    : `+${faNum(ref.rewardPerInvite ?? 0)} ✨`;
  const share = () => {
    haptic('light');
    openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(t('inviteShareText'))}`);
  };
  return (
    <div style={{ padding: '0 18px', marginTop: 14 }}>
      <div className="flm-hairline" style={{
        ...CARD, ...COL, gap: 12, padding: '15px 16px',
        background: 'radial-gradient(120% 120% at 0% 0%, rgba(255,212,145,0.12), transparent 55%), var(--surface)',
        borderColor: 'rgba(255,212,145,0.4)',
      }}>
        <div style={{ ...ROW, gap: 12 }}>
          <span style={{
            width: 38, height: 38, borderRadius: 12, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--accent-tint)', border: '1px solid rgba(255,212,145,0.4)',
          }}>
            <Icon name="gift" size={19} color="var(--accent)" />
          </span>
          <div style={{ ...COL, flex: 1, minWidth: 0 }}>
            <span style={{ ...ROW, gap: 7 }}>
              <b style={{ fontSize: 15 }}>{t('inviteTitle')}</b>
              <span className="flm-mono" style={{
                fontSize: 8.5, color: 'var(--accent)', background: 'var(--accent-tint)',
                border: '1px solid rgba(255,212,145,0.4)', padding: '2px 7px', borderRadius: 99,
              }}>{rewardBadge}</span>
            </span>
            <span style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.35 }}>{subText}</span>
          </div>
        </div>
        <div style={{ ...ROW, gap: 8, justifyContent: 'space-between' }}>
          <span className="flm-mono" style={{ fontSize: 9, color: 'var(--text-3)' }}>
            {t('inviteStats', { a: faNum(ref.invited), b: faNum(ref.rewarded) })}
          </span>
          <button className="flm-cta is-sm flm-press" onClick={share} style={{ gap: 6 }}>
            <Icon name="plane" size={15} fill="currentColor" color="none" /> {t('inviteShare')}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProfileScreen({ t, faNum, lang, setLang, premium, onUpgrade, meName }) {
  const demo = isDemo();
  const [me, setMe] = React.useState(null);
  const [meErr, setMeErr] = React.useState(false);
  const [liveStats, setLiveStats] = React.useState(null); /* GET /v1/me/stats */
  const [ghost, setGhost] = React.useState(false);
  const [boostedUntil, setBoostedUntil] = React.useState(null);
  const [editOpen, setEditOpen] = React.useState(false);
  const [boostOpen, setBoostOpen] = React.useState(false); /* purchase sheet */
  const [strengthOpen, setStrengthOpen] = React.useState(false); /* completeness checklist */
  const [planOpen, setPlanOpen] = React.useState(false); /* Manage-plan sheet */
  const [sub, setSub] = React.useState(null); /* GET /v1/me/subscription */
  /* cheapest plan = the "from N TON" teaser — live catalog; seed prices in demo */
  const [fromTon, setFromTon] = React.useState(() =>
    (demo ? FLM_DATA.plans.reduce((m, p) => Math.min(m, parseFloat(p.priceTon)), Infinity) : null));

  // Live profile + REAL hub stats. Re-run after the edit sheet saves, so the
  // hub reflects the changes. No mock fallback — errors render a retry state.
  const loadMe = React.useCallback(() => {
    if (demo) return;
    setMeErr(false);
    getMe()
      .then((res) => {
        if (!res) return;
        setMe(res);
        setGhost(Boolean(res.profile?.isHidden));
        setBoostedUntil(res.profile?.boostedUntil ?? null);
      })
      .catch(() => setMeErr(true));
    getMyStats().then(setLiveStats).catch(() => {});
    // Subscription drives the Manage-plan sheet (plan/days/perks/limits) and the
    // banner framing; refreshed here so a successful upgrade reflects immediately.
    getSubscription().then(setSub).catch(() => {});
  }, [demo]);
  React.useEffect(() => { loadMe(); }, [loadMe]);

  React.useEffect(() => {
    if (demo) return;
    getPlans()
      .then((res) => {
        const items = res?.items ?? [];
        if (items.length > 0) setFromTon(items.reduce((m, p) => Math.min(m, parseFloat(p.priceTon)), Infinity));
      })
      .catch(() => {}); // banner simply skips the price teaser
  }, [demo]);

  /* ghost mode is server-backed (Profile.isHidden) — persisted fire-and-forget */
  const toggleGhost = () => {
    const next = !ghost;
    setGhost(next);
    if (!demo) patchProfile({ isHidden: next }).catch(() => {});
  };

  /* Boost = a FREE weekly allowance (per plan entitlements.boostsPerWeek) the
     server grants via POST /v1/me/boost, with a paid one-off consumable
     (TON / Stars) only as the fallback once the free allowance is exhausted.
     The row consumes a FREE boost first; if none remain (or the tier grants
     none) the server 403s and we open the paid purchase sheet (+ "or wait"). */
  const boostActive = boostedUntil && new Date(boostedUntil).getTime() > Date.now();
  const [boosting, setBoosting] = React.useState(false); /* free-grant in flight */
  const [boostJustGranted, setBoostJustGranted] = React.useState(false); /* one-shot activation pulse */

  /* free-allowance math off the live subscription (demo seeds a 1-of-3 tier) */
  const boostsPerWeek = demo ? 3 : (sub?.entitlements?.boostsPerWeek ?? 0);
  const boostsUsed = demo ? 0 : (sub?.usage?.boostsThisWeek ?? 0);
  const boostsRemaining = Math.max(0, boostsPerWeek - boostsUsed);
  const hasFreeBoosts = boostsPerWeek > 0; /* tier grants any free boosts at all */

  /* localized "resets …" phrase from boostsResetAt (next ISO-week start) */
  const boostResetWhen = React.useMemo(() => {
    const iso = demo ? new Date(Date.now() + 3 * 86400000).toISOString() : sub?.boostsResetAt;
    if (!iso) return '';
    const reset = new Date(iso);
    const days = Math.max(0, Math.ceil((reset.getTime() - Date.now()) / 86400000));
    if (days <= 0) return t('hubBoostResetDay'); /* resets today/tomorrow boundary */
    if (days === 1) return t('hubBoostResetDay');
    if (days <= 6) return t('hubBoostResetDays', { n: faNum(days) });
    const date = faNum(reset.toLocaleDateString(lang === 'fa' ? 'fa-IR' : 'en-US', { month: 'short', day: 'numeric' }));
    return t('hubBoostResetDate', { date });
  }, [demo, sub, lang, t, faNum]);

  const onBoost = () => {
    if (boostActive || boosting) return; /* already boosted / mid-flight — no double-spend */
    /* no free allowance on this tier, or none left this week → paid sheet */
    if (!hasFreeBoosts || boostsRemaining <= 0) { setBoostOpen(true); return; }
    if (demo) { showBoostActivated(new Date(Date.now() + 30 * 60 * 1000).toISOString()); return; }
    setBoosting(true);
    postBoost()
      .then((res) => { showBoostActivated(res?.boostedUntil); loadMe(); })
      .catch((err) => {
        // 403 = free allowance exhausted server-side (race) → fall back to buy.
        if (err && err.status === 403) setBoostOpen(true);
      })
      .finally(() => setBoosting(false));
  };

  /* shared activation visual — flip boostedUntil locally + pulse the row */
  const showBoostActivated = (until) => {
    if (until) setBoostedUntil(until);
    setBoostJustGranted(true);
    setTimeout(() => setBoostJustGranted(false), 1800);
  };

  /* paid purchase confirmed → server extended boostedUntil; refetch to reflect */
  const onBoosted = () => {
    setBoostOpen(false);
    if (demo) { showBoostActivated(new Date(Date.now() + 30 * 60 * 1000).toISOString()); return; }
    loadMe();
  };
  /* localized "until HH:MM" for the active state */
  const boostUntilLabel = boostedUntil
    ? faNum(new Date(boostedUntil).toLocaleTimeString(lang === 'fa' ? 'fa-IR' : 'en-US', { hour: '2-digit', minute: '2-digit' }))
    : '';

  const pct = demo ? 72 : me?.profileStrength ?? 0; /* server-computed completeness */
  const age = demo ? 27 : me?.profile?.age; /* null → name renders without age */
  const name = me?.profile?.displayName || meName;
  const verified = demo ? true : Boolean(me?.profile?.isVerified); /* never the fake badge */
  /* Effective premium — server truth (me.user.isPremium / subscription) drives
     the badge + banner; the app-level `premium` flag is the demo/just-upgraded
     fallback so the UI flips instantly before the refetch lands. */
  const isPremium = demo ? premium : (sub?.isPremium ?? Boolean(me?.user?.isPremium) ?? premium);
  /* Real plan name from the live subscription payload (getSubscription → plan.name,
     e.g. "Spark"/"Pulse"/"Infinity"). Drives the name-pill + the premium banner
     title; falls back to a generic premium label when the name is missing. */
  const planName = sub?.plan?.name || (isPremium ? t('premiumBadgeFallback') : null);
  /* The plan's own icon (getSubscription → plan.icon: spark='zap', pulse='activity',
     infinity='infinity', free='flame') — drives the banner's leading mark, matching
     the name-row PremiumBadge. Gold crown fallback when the catalog omits it. */
  const planIcon = sub?.plan?.icon || 'crown';
  /* cover photo first, Telegram avatar second, gradient initial last */
  const photos = me?.profile?.photos;
  const cover = photos?.find((ph) => ph.isCover) || photos?.[0];
  const photoUrl = mediaUrl(cover?.url) || me?.telegram?.photoUrl;
  const stats = demo
    ? [[faNum(48), t('hubStatLikes')], [faNum(12), t('hubStatMatches')], [faNum(3), t('hubStatGifts')]]
    : [
        [faNum(liveStats?.likes ?? 0), t('hubStatLikes')],
        [faNum(liveStats?.matches ?? 0), t('hubStatMatches')],
        [faNum(liveStats?.gifts ?? 0), t('hubStatGifts')],
      ];

  /* live mode gates the whole hub on the real profile (loading → error → data) */
  if (!demo && !me) {
    return (
      <div data-screen-label="Your hub" style={{ ...COL, flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
        <Blob c="is-purple" style={{ width: 240, height: 240, top: -60, left: -80 }} />
        {meErr ? (
          <div style={{ ...COL, flex: 1, justifyContent: 'center', padding: 18, position: 'relative', zIndex: 2 }}>
            <StatePanel title={t('errTitle')} note={t('errNote')} actionLabel={t('retry')} onAction={loadMe} />
          </div>
        ) : (
          <div className="flm-scroll" style={{ position: 'relative', zIndex: 2 }}>
            <ProfileSkeleton />
          </div>
        )}
      </div>
    );
  }

  /* ghost-mode switch, integrated into the Privacy row (same pill as the wizard) */
  const ghostSwitch = (
    <button className="flm-ghost-btn" onClick={(e) => { e.stopPropagation(); toggleGhost(); }}
      style={{ width: 40, height: 24, borderRadius: 99, background: ghost ? 'var(--accent)' : 'var(--border-bright)', position: 'relative', flex: 'none', padding: 0, transition: 'background .2s ease' }}>
      <i style={{ position: 'absolute', top: 3, insetInlineStart: ghost ? 19 : 3, width: 18, height: 18, borderRadius: '50%', background: ghost ? '#000' : 'var(--text-3)', transition: 'inset-inline-start .2s ease' }}></i>
    </button>
  );

  /* working language switcher, integrated into the Language row */
  const langSwitch = (
    <span style={{ ...ROW, gap: 2, flex: 'none' }}>
      {[['en', 'EN'], ['fa', 'فا']].map(([id, label]) => (
        <button key={id} className="flm-ghost-btn" onClick={(e) => { e.stopPropagation(); setLang(id); }} style={{ padding: '4px 6px' }}>
          <span className="flm-mono" style={{ fontSize: 9, color: lang === id ? 'var(--accent)' : 'var(--text-3)' }}>{label}</span>
        </button>
      ))}
    </span>
  );

  return (
    <div data-screen-label="Your hub" style={{ ...COL, flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
      <Blob c="is-purple" style={{ width: 240, height: 240, top: -60, left: -80 }} />

      <div className="flm-scroll" style={{ ...COL, position: 'relative', zIndex: 2, paddingTop: 10, paddingBottom: 18 }}>
        {/* identity — strength ring avatar + name + stats */}
        <div style={{ ...COL, alignItems: 'center', gap: 10, padding: '0 18px' }}>
          <div style={{ position: 'relative' }}>
            <span style={{
              position: 'absolute', inset: -5, borderRadius: '50%',
              background: `conic-gradient(var(--accent) 0 ${pct}%, var(--border-bright) ${pct}% 100%)`,
            }}></span>
            <span style={{
              position: 'relative', width: 92, height: 92, borderRadius: '50%',
              border: '4px solid #000', background: G1, overflow: 'hidden',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 35,
              color: 'rgba(255,255,255,.85)', userSelect: 'none',
            }}>
              {photoUrl ? (
                <Img src={photoUrl} eager style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
              ) : (
                <React.Fragment>
                  {(name || '?')[0].toUpperCase()}
                  <span style={{ position: 'absolute', inset: 0, background: AVA_SHADE }}></span>
                </React.Fragment>
              )}
            </span>
            {verified ? (
              <span className="flm-verif" style={{ position: 'absolute', bottom: 0, insetInlineEnd: 0, zIndex: 2, width: 22, height: 22 }}>
                <Icon name="check" size={13} strokeWidth={3} style={{ width: 13, height: 13 }} />
              </span>
            ) : null}
          </div>
          <div style={{ ...COL, alignItems: 'center', gap: 2 }}>
            <span style={{ ...ROW, gap: 8 }}>
              <b className="flm-display" style={{ fontSize: 24 }}>{age == null ? name : `${name}, ${faNum(age)}`}</b>
              {/* premium badge — pill with the plan's icon + REAL plan name (plan.name) */}
              {isPremium ? <PremiumBadge t={t} planName={sub?.plan?.name} planIcon={sub?.plan?.icon} /> : null}
            </span>
            {/* Telegram identity — real handle + linked state only (never a fake
                Premium/verified badge); hidden when the account has no username */}
            {me?.telegram?.username ? (
              <span style={{ ...ROW, gap: 6, marginTop: 1 }}>
                <Icon name="plane" size={12} fill="var(--ton)" color="none" />
                <span style={{ fontSize: 12.5, color: 'var(--text-2)', direction: 'ltr', unicodeBidi: 'embed' }}>@{me.telegram.username}</span>
                <span className="flm-mono" style={{
                  fontSize: 7.5, color: 'var(--text-3)', flex: 'none',
                  border: '1px solid var(--border)', padding: '2px 7px', borderRadius: 99,
                }}>{t('tgLinked')}</span>
              </span>
            ) : null}
            {/* completeness — tappable in live mode: opens the checklist sheet
                with the exact steps to 100% (mirrors the server's strength math) */}
            <button className="flm-ghost-btn" onClick={demo ? undefined : () => setStrengthOpen(true)}
              style={{ ...ROW, gap: 4, padding: '2px 4px', cursor: demo ? 'default' : 'pointer' }}>
              <span className="flm-mono" style={{ fontSize: 9 }}>{t('hubProfileComplete', { pct: faNum(pct) })}</span>
              {!demo ? <Icon name="chevDown" size={11} color="var(--text-3)" /> : null}
            </button>
          </div>
          <div style={{ ...ROW, gap: 26, marginTop: 4 }}>
            {stats.map(([n, l]) => (
              <span key={l} style={{ ...COL, alignItems: 'center' }}>
                <b className="flm-display" style={{ fontSize: 19 }}>{n}</b>
                <span className="flm-mono" style={{ fontSize: 8 }}>{l}</span>
              </span>
            ))}
          </div>
        </div>

        {/* Premium banner — taps open the Manage-plan sheet.
            PREMIUM: the REAL plan's icon (plan.icon → spark='zap', pulse='activity',
              infinity='infinity'; crown fallback) + real plan name + "Active · N
              days left" (days line guarded so a 0 never prints).
            FREE: a generic crown upsell — "Go Premium" + what they'd unlock +
              "Upgrade" (cheapest "from N TON" when the catalog has a price) —
            with NO fake plan name. Both route through onUpgrade (premium ladder). */}
        <div style={{ padding: '0 18px', marginTop: 16 }}>
          <div className="flm-hairline" onClick={() => setPlanOpen(true)} style={{
            ...CARD, ...ROW, gap: 12, padding: '14px 16px',
            background: 'linear-gradient(135deg, var(--primary-tint), transparent 60%)',
            borderColor: 'rgba(122,31,162,.5)', cursor: 'pointer',
          }}>
            {/* leading icon = the plan's own icon when premium (crown fallback); generic crown when free */}
            <Icon name={isPremium ? planIcon : 'crown'} size={22} color="var(--gold)"
              fill={(isPremium ? planIcon : 'crown') === 'crown' ? 'var(--gold)' : 'none'} style={{ flex: 'none' }} />
            <div style={{ ...COL, flex: 1, minWidth: 0 }}>
              {/* title = real plan name when premium (e.g. "Spark"); free → "Go Premium" upsell (never a fake plan) */}
              <b style={{ fontSize: 14.5 }}>{isPremium && planName ? planName : t('hubGoPremium')}</b>
              <span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
                {isPremium
                  ? (sub?.daysRemaining > 0
                      ? (sub.daysRemaining === 1
                          ? `${t('hubPremiumActive')} · ${t('mpDayLeft')}`
                          : `${t('hubPremiumActive')} · ${t('mpDaysLeft', { n: faNum(sub.daysRemaining) })}`)
                      : t('hubPremiumActive'))
                  : t('hubGoPremiumSub')}
              </span>
            </div>
            <span className="flm-mono" style={{ ...ROW, gap: 4, fontSize: 9, color: 'var(--accent)', textAlign: 'end', flex: 'none' }}>
              {isPremium
                ? t('hubPremiumActive')
                : (fromTon != null && Number.isFinite(fromTon) ? t('hubFromTon', { n: faNum(fromTon) }) : t('upgrade'))}
              <span className="flm-flip" style={{ ...ROW, color: 'var(--text-3)' }}><Icon name="chevRight" size={13} /></span>
            </span>
          </div>
        </div>

        {/* invite & earn — referral share + reward */}
        {!demo ? <InviteCard t={t} faNum={faNum} /> : null}

        {/* settings rows */}
        <div style={{ padding: '0 18px', marginTop: 14 }}>
          <div style={{ ...CARD, ...COL, overflow: 'hidden' }}>
            <Item icon={<Icon name="pencil" size={17} />}
              label={t('editProfile')} sub={t('epSub')} onClick={() => setEditOpen(true)} />
            <Hr />
            <Item icon={<Icon name="camera" size={17} />}
              label={t('hubEditPhotos')}
              sub={demo || me?.profile?.hasVoiceIntro
                ? t('hubEditPhotosSub', { n: faNum(photos?.length ?? (demo ? 4 : 0)) })
                : `${faNum(photos?.length ?? 0)} ${t('photos').toLowerCase()}`}
              onClick={() => setEditOpen(true)} />
            <Hr />
            {/* verification — flow not built yet (owner: keep the row, mark it
                "coming soon"). De-emphasized, non-interactive (no onClick), and a
                muted "Coming soon" chip replaces the tappable chevron. */}
            <div style={{ opacity: 0.55 }}>
              <Item icon={<Icon name="shield" size={17} />}
                label={t('hubVerification')}
                sub={t('hubNotVerified')}
                trailing={(
                  <span className="flm-mono" style={{
                    fontSize: 8.5, color: 'var(--text-3)', flex: 'none',
                    border: '1px solid var(--border)', padding: '3px 8px', borderRadius: 99,
                  }}>{t('comingSoon')}</span>
                )} />
            </div>
            <Hr />
            <BoostRow t={t} faNum={faNum}
              hasFreeBoosts={hasFreeBoosts} perWeek={boostsPerWeek} used={boostsUsed}
              remaining={boostsRemaining} resetWhen={boostResetWhen}
              active={boostActive} untilLabel={boostUntilLabel}
              boosting={boosting} justGranted={boostJustGranted} onClick={onBoost} />
            <Hr />
            {GHOST_ENABLED ? (
              /* parked per owner feedback — flip GHOST_ENABLED to restore */
              <React.Fragment>
                <Item icon={<Icon name="eyeOff" size={17} />}
                  label={t('hubPrivacy')} sub={t(ghost ? 'ghostOn' : 'hubPrivacySub')} trailing={ghostSwitch} />
                <Hr />
              </React.Fragment>
            ) : null}
            <Item icon={<Icon name="globe" size={17} />}
              label={t('language')} sub={t('hubLanguageSub')} trailing={langSwitch} />
            {/* wallet / TON Connect row — hidden per owner decision (gated, not
                deleted). Its leading divider is gated too so the Language row
                stays the clean last row when the wallet row is off. */}
            {TON_CONNECT_ROW_ENABLED ? (
              <React.Fragment>
                <Hr />
                {/* payments are invoice-based and non-custodial: no stored
                    balance exists, so live mode never shows an amount chip */}
                {demo ? (
                  <Item icon={<TonMark size={17} />} accent="var(--ton)"
                    label={t('wallet')} sub={t('hubWalletSub')} badge={t('hubTon', { n: faNum('2.4') })} />
                ) : (
                  <Item icon={<TonMark size={17} />} accent="var(--ton)"
                    label={t('hubWalletTitle')} sub={t('hubWalletSubLive')} />
                )}
              </React.Fragment>
            ) : null}
          </div>
        </div>
      </div>

      {boostOpen ? (
        <React.Suspense fallback={null}>
          <BoostSheet t={t} faNum={faNum} demo={demo}
            orWaitWhen={hasFreeBoosts ? boostResetWhen : ''}
            onClose={() => setBoostOpen(false)} onBoosted={onBoosted} />
        </React.Suspense>
      ) : null}
      <EditProfileSheet open={editOpen} t={t} faNum={faNum} me={me}
        onClose={() => setEditOpen(false)} onSaved={loadMe} />
      <StrengthSheet open={strengthOpen} t={t} faNum={faNum} me={me} pct={pct}
        onClose={() => setStrengthOpen(false)}
        onFix={() => { setStrengthOpen(false); setEditOpen(true); /* every fix lives in the edit sheet */ }} />
      <ManagePlanSheet open={planOpen} t={t} faNum={faNum} sub={sub} isPremium={isPremium} lang={lang}
        onClose={() => setPlanOpen(false)}
        onUpgrade={() => { setPlanOpen(false); onUpgrade(); /* existing premium checkout */ }} />
    </div>
  );
}

export { ProfileScreen };
